import {
  supabaseClient,
} from "../../services/supabase";

import type {
  Aluno,
  AlunoDaTurma,
  DiaSemana,
  NovaTurmaInput,
  NovoAlunoInput,
  QuadraOption,
  TipoTurma,
  Turma,
  TurmaAluno,
} from "./types";


type TurmaRow = {
  id: string;

  nome: string;

  professor: string | null;

  dias: DiaSemana[];

  hora: string;

  nivel: string;

  capacidade: number;

  tipo: TipoTurma;

  quadra_id: string | null;

  duracao: number;

  bloquear_quadra: boolean;

  ativa: boolean;

  created_at: string;

  updated_at: string;
};


type QuadraRow = {
  id: string;

  nome: string;

  ativa: boolean;
};


type AlunoRow = {
  id: string;

  nome: string;

  nivel: string;

  telefone: string | null;

  nascimento: string | null;

  ativo: boolean;

  created_at: string;

  updated_at: string;
};


type TurmaAlunoRow = {
  id: string;

  turma_id: string;

  aluno_id: string;

  ativo: boolean;

  created_at: string;
};

/*
 * =========================================================
 * TURMAS
 * =========================================================
 */

export async function loadTurmas(): Promise<
  Turma[]
> {
  const {
    data: turmasData,
    error: turmasError,
  } =
    await supabaseClient
      .from("turmas")
      .select(
        `
          id,
          nome,
          professor,
          dias,
          hora,
          nivel,
          capacidade,
          tipo,
          quadra_id,
          duracao,
          bloquear_quadra,
          ativa,
          created_at,
          updated_at
        `,
      )
      .order(
        "created_at",
        {
          ascending: true,
        },
      );


  if (turmasError) {
    throw turmasError;
  }


  const {
    data: quadrasData,
    error: quadrasError,
  } =
    await supabaseClient
      .from("quadras")
      .select(
        "id,nome,ativa",
      );


  if (quadrasError) {
    throw quadrasError;
  }


  const quadras =
    (quadrasData ??
      []) as QuadraRow[];


  const quadraNomePorId =
    new Map<
      string,
      string
    >();


  quadras.forEach(
    (quadra) => {
      quadraNomePorId.set(
        quadra.id,
        quadra.nome,
      );
    },
  );


  const turmas =
    (turmasData ??
      []) as TurmaRow[];


  return turmas.map(
    (turma) => ({
      ...turma,

      dias:
        turma.dias ??
        [],

      capacidade:
        Number(
          turma.capacidade,
        ),

      duracao:
        Number(
          turma.duracao,
        ),

      bloquear_quadra:
        turma.bloquear_quadra ??
        true,

      quadra_nome:
        turma.quadra_id
          ? quadraNomePorId.get(
              turma.quadra_id,
            ) ?? null
          : null,
    }),
  );
}


export async function loadQuadrasAtivas(): Promise<
  QuadraOption[]
> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .from("quadras")
      .select(
        "id,nome",
      )
      .eq(
        "ativa",
        true,
      )
      .order(
        "created_at",
        {
          ascending: true,
        },
      );


  if (error) {
    throw error;
  }


  return (
    data ??
    []
  ) as QuadraOption[];
}


export async function createTurma(
  input: NovaTurmaInput,
): Promise<Turma> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "criar_turma",
      {
        p_nome:
          input.nome,

        p_professor:
          input.professor,

        p_dias:
          input.dias,

        p_hora:
          input.hora,

        p_nivel:
          input.nivel,

        p_capacidade:
          input.capacidade,

        p_tipo:
          input.tipo,

        p_quadra_id:
          input.quadra_id,

        p_duracao:
          input.duracao,

        p_bloquear_quadra:
          input.bloquear_quadra,
      },
    );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "Não foi possível cadastrar a turma.",
    );
  }

  const result =
    data as {
      success: boolean;
      turma: TurmaRow;
    };

  const row =
    result.turma;

  return {
    ...row,

    capacidade:
      Number(
        row.capacidade,
      ),

    duracao:
      Number(
        row.duracao,
      ),

    bloquear_quadra:
      Boolean(
        row.bloquear_quadra,
      ),

    quadra_nome:
      null,
  };
}


/*
 * =========================================================
 * ALUNOS
 * =========================================================
 */

export async function loadAlunos(): Promise<
  Aluno[]
> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .from("alunos")
      .select(
        `
          id,
          nome,
          nivel,
          telefone,
          nascimento,
          ativo,
          created_at,
          updated_at
        `,
      )
      .eq(
        "ativo",
        true,
      )
      .order(
        "nome",
        {
          ascending: true,
        },
      );


  if (error) {
    throw error;
  }


  const rows =
    (data ??
      []) as AlunoRow[];


  return rows.map(
    (row) => ({
      ...row,

      ativo:
        Boolean(
          row.ativo,
        ),
    }),
  );
}


export async function createAluno(
  input: NovoAlunoInput,
): Promise<Aluno> {
  const nome =
    input.nome.trim();

  const telefone =
    input.telefone.trim();


  if (!nome) {
    throw new Error(
      "Informe o nome do aluno.",
    );
  }


  const {
    data,
    error,
  } =
    await supabaseClient
      .from("alunos")
      .insert({
        nome,

        nivel:
          input.nivel,

        telefone:
          telefone ||
          null,

        nascimento:
          input.nascimento ||
          null,

        ativo:
          true,

        updated_at:
          new Date()
            .toISOString(),
      })
      .select(
        `
          id,
          nome,
          nivel,
          telefone,
          nascimento,
          ativo,
          created_at,
          updated_at
        `,
      )
      .single();


  if (error) {
    throw error;
  }


  return data as Aluno;
}


/*
 * =========================================================
 * ALUNOS DA TURMA
 * =========================================================
 */

export async function loadAlunosDaTurma(
  turmaId: string,
): Promise<AlunoDaTurma[]> {
  /*
   * Primeiro buscamos os vínculos.
   */

  const {
    data:
      vinculosData,

    error:
      vinculosError,
  } =
    await supabaseClient
      .from(
        "turma_alunos",
      )
      .select(
        `
          id,
          turma_id,
          aluno_id,
          ativo,
          created_at
        `,
      )
      .eq(
        "turma_id",
        turmaId,
      )
      .eq(
        "ativo",
        true,
      )
      .order(
        "created_at",
        {
          ascending: true,
        },
      );


  if (vinculosError) {
    throw vinculosError;
  }


  const vinculos =
    (vinculosData ??
      []) as TurmaAlunoRow[];


  if (
    vinculos.length ===
    0
  ) {
    return [];
  }


  /*
   * Depois buscamos os alunos desses vínculos.
   */

  const alunoIds =
    vinculos.map(
      (vinculo) =>
        vinculo.aluno_id,
    );


  const {
    data:
      alunosData,

    error:
      alunosError,
  } =
    await supabaseClient
      .from("alunos")
      .select(
        `
          id,
          nome,
          nivel,
          telefone,
          nascimento,
          ativo,
          created_at,
          updated_at
        `,
      )
      .in(
        "id",
        alunoIds,
      );


  if (alunosError) {
    throw alunosError;
  }


  const alunos =
    (alunosData ??
      []) as AlunoRow[];


  const alunoPorId =
    new Map<
      string,
      Aluno
    >();


  alunos.forEach(
    (aluno) => {
      alunoPorId.set(
        aluno.id,
        {
          ...aluno,

          ativo:
            Boolean(
              aluno.ativo,
            ),
        },
      );
    },
  );


  return vinculos
    .map<AlunoDaTurma | null>(
      (vinculo) => {
        const aluno =
          alunoPorId.get(
            vinculo.aluno_id,
          );


        if (!aluno) {
          return null;
        }


        return {
          vinculo_id:
            vinculo.id,

          aluno,
        };
      },
    )
    .filter(
      (
        item,
      ): item is AlunoDaTurma =>
        item !== null,
    );
}


/*
 * =========================================================
 * ADICIONAR ALUNO À TURMA
 * =========================================================
 */

export async function adicionarAlunoNaTurma(
  turmaId: string,
  alunoId: string,
): Promise<TurmaAluno> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "adicionar_aluno_turma",
      {
        p_turma_id:
          turmaId,

        p_aluno_id:
          alunoId,
      },
    );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "Não foi possível adicionar o aluno à turma.",
    );
  }

  const result =
    data as {
      success: boolean;
      vinculo: TurmaAlunoRow;
      total_ativos: number;
      capacidade: number;
    };

  return result.vinculo as TurmaAluno;
}


/*
 * =========================================================
 * REMOVER ALUNO DA TURMA
 * =========================================================
 *
 * Não apagamos o histórico.
 * Apenas tornamos o vínculo inativo.
 * =========================================================
 */

export async function removerAlunoDaTurma(
  vinculoId: string,
) {
  const {
    error,
  } =
    await supabaseClient
      .from(
        "turma_alunos",
      )
      .update({
        ativo:
          false,
      })
      .eq(
        "id",
        vinculoId,
      );


  if (error) {
    throw error;
  }
}
/*
 * =========================================================
 * PACOTES DE AULAS
 * =========================================================
 */

export type PacoteAulas = {
  id: string;

  aluno_id: string;

  turma_id: string | null;

  quantidade_aulas: number;

  aulas_usadas: number;

  valor: number;

  status: string;

  pago: boolean;

  forma_pagamento: string | null;

  pago_em: string | null;

  created_at: string;

  encerrado_em: string | null;
};


/*
 * =========================================================
 * BUSCAR PACOTE ATIVO DO ALUNO
 * =========================================================
 */

export async function loadPacoteAtivo(
  alunoId: string,
  turmaId: string,
): Promise<PacoteAulas | null> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .from("pacotes_aulas")
      .select(
        `
          id,
          aluno_id,
          turma_id,
          quantidade_aulas,
          aulas_usadas,
          valor,
          status,
          pago,
          forma_pagamento,
          pago_em,
          created_at,
          encerrado_em
        `,
      )
      .eq(
        "aluno_id",
        alunoId,
      )
      .eq(
        "turma_id",
        turmaId,
      )
      .eq(
        "status",
        "ativo",
      )
      .order(
        "created_at",
        {
          ascending: false,
        },
      )
      .limit(1)
      .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return null;
  }

  return {
    ...data,

    quantidade_aulas:
      Number(
        data.quantidade_aulas,
      ),

    aulas_usadas:
      Number(
        data.aulas_usadas,
      ),

    valor:
      Number(
        data.valor,
      ),

    pago:
      Boolean(
        data.pago,
      ),
  } as PacoteAulas;
}


/*
 * =========================================================
 * CRIAR PACOTE
 * =========================================================
 */

export async function criarPacote(
  alunoId: string,
  turmaId: string,
  quantidadeAulas: number,
  valor: number,
): Promise<PacoteAulas> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "criar_pacote_aulas",
      {
        p_aluno_id:
          alunoId,

        p_turma_id:
          turmaId,

        p_quantidade_aulas:
          quantidadeAulas,

        p_valor:
          valor,
      },
    );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "Não foi possível criar o pacote.",
    );
  }

  return {
    ...data,

    quantidade_aulas:
      Number(
        data.quantidade_aulas,
      ),

    aulas_usadas:
      Number(
        data.aulas_usadas,
      ),

    valor:
      Number(
        data.valor,
      ),

    pago:
      Boolean(
        data.pago,
      ),
  } as PacoteAulas;
}

export async function editarTurma(input: {
  turmaId: string;

  nome: string;

  professor: string;

  dias: DiaSemana[];

  hora: string;

  nivel: string;

  capacidade: number;

  tipo: TipoTurma;

  quadraId: string;

  duracao: number;

  bloquearQuadra: boolean;

  updatedAtEsperado?: string | null;
}): Promise<Turma> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "editar_turma",
      {
        p_turma_id:
          input.turmaId,

        p_nome:
          input.nome,

        p_professor:
          input.professor,

        p_dias:
          input.dias,

        p_hora:
          input.hora,

        p_nivel:
          input.nivel,

        p_capacidade:
          input.capacidade,

        p_tipo:
          input.tipo,

        p_quadra_id:
          input.quadraId,

        p_duracao:
          input.duracao,

        p_bloquear_quadra:
          input.bloquearQuadra,

        p_updated_at_esperado:
          input.updatedAtEsperado ??
          null,
      },
    );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "Não foi possível editar a turma.",
    );
  }

  const result =
    data as {
      success: boolean;
      turma: TurmaRow;
    };

  const row =
    result.turma;

  return {
    ...row,

    capacidade:
      Number(
        row.capacidade,
      ),

    duracao:
      Number(
        row.duracao,
      ),

    bloquear_quadra:
      Boolean(
        row.bloquear_quadra,
      ),

    quadra_nome:
      null,
  };
}


export async function desativarTurma(
  turmaId: string,
  updatedAtEsperado?: string | null,
): Promise<Turma> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "desativar_turma",
      {
        p_turma_id:
          turmaId,

        p_updated_at_esperado:
          updatedAtEsperado ??
          null,
      },
    );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "Não foi possível encerrar a turma.",
    );
  }

  const result =
    data as {
      success: boolean;
      turma: TurmaRow;
    };

  const row =
    result.turma;

  return {
    ...row,

    capacidade:
      Number(
        row.capacidade,
      ),

    duracao:
      Number(
        row.duracao,
      ),

    bloquear_quadra:
      Boolean(
        row.bloquear_quadra,
      ),

    quadra_nome:
      null,
  };
}

export async function reativarTurma(
  turmaId: string,
  updatedAtEsperado?: string | null,
): Promise<Turma> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "reativar_turma",
      {
        p_turma_id:
          turmaId,

        p_updated_at_esperado:
          updatedAtEsperado ??
          null,
      },
    );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "Não foi possível reativar a turma.",
    );
  }

  const result =
    data as {
      success: boolean;
      turma: TurmaRow;
    };

  const row =
    result.turma;

  return {
    ...row,

    capacidade:
      Number(
        row.capacidade,
      ),

    duracao:
      Number(
        row.duracao,
      ),

    bloquear_quadra:
      Boolean(
        row.bloquear_quadra,
      ),

    quadra_nome:
      null,
  };
}


/*
 * =========================================================
 * EDITAR PACOTE
 * =========================================================
 */

export async function editarPacote(
  pacoteId: string,
  quantidadeAulas: number,
  valor: number,
): Promise<PacoteAulas> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "editar_pacote_aulas",
      {
        p_pacote_id:
          pacoteId,

        p_quantidade_aulas:
          quantidadeAulas,

        p_valor:
          valor,
      },
    );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "Não foi possível editar o pacote.",
    );
  }

  return {
    ...data,

    quantidade_aulas:
      Number(
        data.quantidade_aulas,
      ),

    aulas_usadas:
      Number(
        data.aulas_usadas,
      ),

    valor:
      Number(
        data.valor,
      ),

    pago:
      Boolean(
        data.pago,
      ),
  } as PacoteAulas;
}
export type FormaPagamentoAula =
  | "pix"
  | "dinheiro"
  | "debito"
  | "credito";


export type PagamentoPacoteResult = {
  success: boolean;

  pacote_id: string;

  aluno_id: string;

  turma_id: string | null;

  valor: number;

  forma_pagamento:
    FormaPagamentoAula;

  pago_em: string;

  movimento_id: string;
};


export async function pagarPacote(
  pacoteId: string,
  formaPagamento:
    FormaPagamentoAula,
): Promise<PagamentoPacoteResult> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "pagar_pacote_aulas",
      {
        p_pacote_id:
          pacoteId,

        p_forma_pagamento:
          formaPagamento,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "O pagamento não retornou os dados esperados.",
    );
  }


  return {
    ...data,

    valor:
      Number(
        data.valor,
      ),
  } as PagamentoPacoteResult;
}
export type Aula = {
  id: string;

  turma_id: string;

  data: string;

  hora: number;

  created_at: string;
};


export type PresencaAula = {
  id: string;

  aula_id: string;

  aluno_id: string;

  presente: boolean;

  tipo_pagamento:
    | "pacote"
    | "avulsa";

  valor_avulso: number;

  pago: boolean;

  forma_pagamento: string | null;

  pago_em: string | null;

  pacote_id: string | null;

  created_at: string;
};


function timeStringToNumber(
  value: string,
) {
  const [
    hours,
    minutes,
  ] =
    value
      .split(":")
      .map(Number);

  return (
    hours +
    (minutes || 0) / 60
  );
}


export async function obterOuCriarAula(
  turmaId: string,
  data: string,
  hora: string,
): Promise<Aula> {
  const {
    data: result,
    error,
  } =
    await supabaseClient.rpc(
      "obter_ou_criar_aula",
      {
        p_turma_id:
          turmaId,

        p_data:
          data,

        p_hora:
          timeStringToNumber(
            hora,
          ),
      },
    );


  if (error) {
    throw error;
  }


  if (!result) {
    throw new Error(
      "Não foi possível criar ou localizar a aula.",
    );
  }


  return {
    ...result,

    hora:
      Number(
        result.hora,
      ),
  } as Aula;
}


export async function registrarPresenca(
  aulaId: string,
  alunoId: string,
  presente: boolean,
  tipo:
    | "pacote"
    | "avulsa",
  valor: number,
  pacoteId:
    string | null,
): Promise<PresencaAula> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "registrar_presenca_aula",
      {
        p_aula_id:
          aulaId,

        p_aluno_id:
          alunoId,

        p_presente:
          presente,

        p_tipo_pagamento:
          tipo,

        p_valor:
          valor,

        p_pacote_id:
          pacoteId,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "Não foi possível registrar a presença.",
    );
  }


  return {
  ...data,

  valor_avulso:
    Number(
      data.valor_avulso ??
        0,
    ),

  pago:
    Boolean(
      data.pago,
    ),

  presente:
    Boolean(
      data.presente,
    ),
} as PresencaAula;
}
export type PagamentoAulaAvulsaResult = {
  success: boolean;

  presenca_id: string;

  aluno_id: string;

  valor: number;

  forma_pagamento:
    FormaPagamentoAula;

  pago_em: string;

  movimento_id: string;
};


export async function pagarAulaAvulsa(
  presencaId: string,
  formaPagamento:
    FormaPagamentoAula,
): Promise<PagamentoAulaAvulsaResult> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "pagar_aula_avulsa",
      {
        p_presenca_id:
          presencaId,

        p_forma_pagamento:
          formaPagamento,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "O pagamento da aula avulsa não retornou os dados esperados.",
    );
  }


  return {
    ...data,

    valor:
      Number(
        data.valor,
      ),
  } as PagamentoAulaAvulsaResult;
}
export type ChamadaExistente = {
  aula: Aula;

  presencas: PresencaAula[];
};
export async function loadAulaExistente(
  turmaId: string,
  data: string,
  hora: string,
): Promise<Aula | null> {
  const horaNumero =
    timeStringToNumber(
      hora,
    );


  const {
    data: result,
    error,
  } =
    await supabaseClient
      .from("aulas")
      .select(
        `
          id,
          turma_id,
          data,
          hora,
          created_at
        `,
      )
      .eq(
        "turma_id",
        turmaId,
      )
      .eq(
        "data",
        data,
      )
      .eq(
        "hora",
        horaNumero,
      )
      .maybeSingle();


  if (error) {
    throw error;
  }


  if (!result) {
    return null;
  }


  return {
    ...result,

    hora:
      Number(
        result.hora,
      ),
  } as Aula;
}
export async function loadPresencasDaAula(
  aulaId: string,
): Promise<PresencaAula[]> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .from(
        "presencas_aulas",
      )
      .select(
        `
          id,
          aula_id,
          aluno_id,
          presente,
          tipo_pagamento,
          valor_avulso,
          pago,
          forma_pagamento,
          pago_em,
          pacote_id,
          created_at
        `,
      )
      .eq(
        "aula_id",
        aulaId,
      )
      .order(
        "created_at",
        {
          ascending: true,
        },
      );


  if (error) {
    throw error;
  }


  return (
    data ??
    []
  ).map(
    (item) => ({
      ...item,

      presente:
        Boolean(
          item.presente,
        ),

      valor_avulso:
        Number(
          item.valor_avulso ??
            0,
        ),

      pago:
        Boolean(
          item.pago,
        ),
    }),
  ) as PresencaAula[];
}
export async function loadChamadaExistente(
  turmaId: string,
  data: string,
  hora: string,
): Promise<ChamadaExistente | null> {
  const aula =
    await loadAulaExistente(
      turmaId,
      data,
      hora,
    );


  if (!aula) {
    return null;
  }


  const presencas =
    await loadPresencasDaAula(
      aula.id,
    );


  return {
    aula,
    presencas,
  };
}
export type HistoricoAulaTurma = {
  aulaId: string;

  data: string;

  hora: number;

  presentes: number;

  faltas: number;

  avulsas: number;

  valorAvulsas: number;
};


export async function loadHistoricoTurma(
  turmaId: string,
): Promise<HistoricoAulaTurma[]> {
  const {
    data: aulasData,
    error: aulasError,
  } =
    await supabaseClient
      .from("aulas")
      .select(
        `
          id,
          turma_id,
          data,
          hora,
          created_at
        `,
      )
      .eq(
        "turma_id",
        turmaId,
      )
      .order(
        "data",
        {
          ascending: false,
        },
      )
      .order(
        "hora",
        {
          ascending: false,
        },
      );


  if (aulasError) {
    throw aulasError;
  }


  const aulas =
    aulasData ??
    [];


  if (
    aulas.length === 0
  ) {
    return [];
  }


  const aulaIds =
    aulas.map(
      (aula) =>
        aula.id,
    );


  const {
    data: presencasData,
    error: presencasError,
  } =
    await supabaseClient
      .from(
        "presencas_aulas",
      )
      .select(
        `
          id,
          aula_id,
          presente,
          tipo_pagamento,
          valor_avulso
        `,
      )
      .in(
        "aula_id",
        aulaIds,
      );


  if (presencasError) {
    throw presencasError;
  }


  const presencas =
    presencasData ??
    [];


  return aulas.map(
    (aula) => {
      const daAula =
        presencas.filter(
          (item) =>
            item.aula_id ===
            aula.id,
        );


      const presentes =
        daAula.filter(
          (item) =>
            Boolean(
              item.presente,
            ),
        ).length;


      const faltas =
        daAula.filter(
          (item) =>
            !Boolean(
              item.presente,
            ),
        ).length;


      const avulsas =
        daAula.filter(
          (item) =>
            item.presente ===
              true &&
            item.tipo_pagamento ===
              "avulsa",
        );


      const valorAvulsas =
        avulsas.reduce(
          (
            total,
            item,
          ) =>
            total +
            Number(
              item.valor_avulso ??
                0,
            ),
          0,
        );


      return {
        aulaId:
          aula.id,

        data:
          aula.data,

        hora:
          Number(
            aula.hora,
          ),

        presentes,

        faltas,

        avulsas:
          avulsas.length,

        valorAvulsas,
      };
    },
  );
}
export type HistoricoAlunoAula = {
  aulaId: string;

  data: string;

  hora: number;

  presente: boolean;

  tipoPagamento:
    | "pacote"
    | "avulsa";

  valorAvulso: number;

  pago: boolean;

  formaPagamento: string | null;

  pacoteId: string | null;
};


export type HistoricoAlunoResumo = {
  totalAulas: number;

  presencas: number;

  faltas: number;

  frequencia: number;

  aulasPacote: number;

  aulasAvulsas: number;

  valorAvulsas: number;

  aulas: HistoricoAlunoAula[];
};
export async function loadHistoricoAluno(
  alunoId: string,
  turmaId: string,
): Promise<HistoricoAlunoResumo> {
  const {
    data: presencasData,
    error: presencasError,
  } =
    await supabaseClient
      .from(
        "presencas_aulas",
      )
      .select(
        `
          id,
          aula_id,
          aluno_id,
          presente,
          tipo_pagamento,
          valor_avulso,
          pago,
          forma_pagamento,
          pacote_id,
          created_at
        `,
      )
      .eq(
        "aluno_id",
        alunoId,
      );


  if (presencasError) {
    throw presencasError;
  }


  const presencas =
    presencasData ??
    [];


  if (
    presencas.length ===
    0
  ) {
    return {
      totalAulas: 0,

      presencas: 0,

      faltas: 0,

      frequencia: 0,

      aulasPacote: 0,

      aulasAvulsas: 0,

      valorAvulsas: 0,

      aulas: [],
    };
  }


  const aulaIds =
    presencas.map(
      (item) =>
        item.aula_id,
    );


  const {
    data: aulasData,
    error: aulasError,
  } =
    await supabaseClient
      .from("aulas")
      .select(
        `
          id,
          turma_id,
          data,
          hora
        `,
      )
      .in(
        "id",
        aulaIds,
      )
      .eq(
        "turma_id",
        turmaId,
      )
      .order(
        "data",
        {
          ascending:
            false,
        },
      )
      .order(
        "hora",
        {
          ascending:
            false,
        },
      );


  if (aulasError) {
    throw aulasError;
  }


  const aulas =
    aulasData ??
    [];


  const aulaPorId =
    new Map(
      aulas.map(
        (aula) => [
          aula.id,
          aula,
        ],
      ),
    );


  const historico =
    presencas
      .filter(
        (presenca) =>
          aulaPorId.has(
            presenca.aula_id,
          ),
      )
      .map(
        (
          presenca,
        ): HistoricoAlunoAula => {
          const aula =
            aulaPorId.get(
              presenca.aula_id,
            )!;


          return {
            aulaId:
              aula.id,

            data:
              aula.data,

            hora:
              Number(
                aula.hora,
              ),

            presente:
              Boolean(
                presenca.presente,
              ),

            tipoPagamento:
              presenca.tipo_pagamento as
                | "pacote"
                | "avulsa",

            valorAvulso:
              Number(
                presenca.valor_avulso ??
                  0,
              ),

            pago:
              Boolean(
                presenca.pago,
              ),

            formaPagamento:
              presenca.forma_pagamento ??
              null,

            pacoteId:
              presenca.pacote_id ??
              null,
          };
        },
      )
      .sort(
        (
          first,
          second,
        ) => {
          const firstKey =
            `${first.data}-${String(
              first.hora,
            ).padStart(
              5,
              "0",
            )}`;

          const secondKey =
            `${second.data}-${String(
              second.hora,
            ).padStart(
              5,
              "0",
            )}`;


          return secondKey.localeCompare(
            firstKey,
          );
        },
      );


  const totalAulas =
    historico.length;


  const presencasCount =
    historico.filter(
      (item) =>
        item.presente,
    ).length;


  const faltas =
    totalAulas -
    presencasCount;


  const aulasPacote =
    historico.filter(
      (item) =>
        item.presente &&
        item.tipoPagamento ===
          "pacote",
    ).length;


  const avulsas =
    historico.filter(
      (item) =>
        item.presente &&
        item.tipoPagamento ===
          "avulsa",
    );


  const valorAvulsas =
    avulsas.reduce(
      (
        total,
        item,
      ) =>
        total +
        item.valorAvulso,
      0,
    );


  const frequencia =
    totalAulas > 0
      ? (
          presencasCount /
          totalAulas
        ) *
        100
      : 0;


  return {
    totalAulas,

    presencas:
      presencasCount,

    faltas,

    frequencia,

    aulasPacote,

    aulasAvulsas:
      avulsas.length,

    valorAvulsas,

    aulas:
      historico,
  };
}
export async function loadPacotesAluno(
  alunoId: string,
  turmaId: string,
): Promise<PacoteAulas[]> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .from("pacotes_aulas")
      .select(
        `
          id,
          aluno_id,
          turma_id,
          quantidade_aulas,
          aulas_usadas,
          valor,
          status,
          pago,
          forma_pagamento,
          pago_em,
          created_at,
          encerrado_em
        `,
      )
      .eq(
        "aluno_id",
        alunoId,
      )
      .eq(
        "turma_id",
        turmaId,
      )
      .order(
        "created_at",
        {
          ascending: false,
        },
      );


  if (error) {
    throw error;
  }


  return (
    data ??
    []
  ).map(
    (item) => ({
      ...item,

      quantidade_aulas:
        Number(
          item.quantidade_aulas,
        ),

      aulas_usadas:
        Number(
          item.aulas_usadas,
        ),

      valor:
        Number(
          item.valor,
        ),

      pago:
        Boolean(
          item.pago,
        ),
    }),
  ) as PacoteAulas[];
}